import { createSignal, createEffect, For, Show, createMemo } from "solid-js";
import { useAuth } from "../contexts/AuthContext";
import { apiUrl } from "../api/base";
import { getProfileConfig } from "../config/profiles";
import { getContactTypeMetasForProfile } from "../config/profileRegistry";

type TemplateQuestion = {
  key: string;
  question: string;
  priority: "Critical" | "Important" | "Informational";
  importance: string;
};

export default function AdminQuestionsPage() {
  const auth = useAuth();

  // Use the logged-in user's assigned profile (no switching allowed)
  const userProfile = () => auth.user()?.assignedProfile || "colorado";

  const [selectedContactType, setSelectedContactType] = createSignal("Intake");
  const [questions, setQuestions] = createSignal<TemplateQuestion[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  // Get the current profile config for styling
  const currentProfile = createMemo(() => getProfileConfig(userProfile()));

  // Get profile-specific contact types (labels differ per profile!)
  const contactTypes = createMemo(() =>
    getContactTypeMetasForProfile(userProfile()),
  );

  // Fetch questions when contact type changes
  createEffect(() => {
    const profile = userProfile();
    const contactType = selectedContactType();
    fetchQuestions(profile, contactType);
  });

  async function fetchQuestions(profile: string, contactType: string) {
    setLoading(true);
    setError(null);
    try {
      // Use includeProfile: false since we're manually specifying the user's assigned profile
      const res = await fetch(
        apiUrl(
          `/api/templateQuestions?profile=${profile}&contactType=${contactType}`,
          { includeProfile: false },
        ),
      );
      if (!res.ok) {
        throw new Error(await res.text());
      }
      const data = await res.json();
      setQuestions(data.questions || []);
    } catch (e: any) {
      setError(e.message || "Failed to fetch questions");
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }

  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case "Critical":
        return "bg-red-100 text-red-800 border-red-200";
      case "Important":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "Informational":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div class="h-screen overflow-y-auto bg-gray-50">
      {/* Header */}
      <header class="bg-blue-500 text-white py-4 shadow">
        <div class="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <h1 class="text-2xl font-bold tracking-wide">
            IntelliCoach - Template Questions
          </h1>
          <div class="flex items-center gap-4">
            <span class="text-sm opacity-90">{auth.user()?.name}</span>
            <button
              onClick={() => auth.logout()}
              class="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm font-medium transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main class="max-w-7xl mx-auto px-4 py-6">
        {/* Controls */}
        <div class="bg-white rounded-lg shadow p-4 mb-6">
          <div class="flex flex-wrap items-center gap-6">
            {/* Profile Display (locked to user's assigned profile) */}
            <div class="flex items-center gap-2">
              <label class="text-sm font-medium text-gray-700">Profile:</label>
              <span
                class="px-3 py-2 rounded-md text-sm font-medium text-white"
                style={{
                  "background-color":
                    currentProfile()?.accentColor || "#3B82F6",
                }}
              >
                {currentProfile()?.name}
              </span>
            </div>

            {/* Contact Type Tabs - Labels change per profile! */}
            <div class="flex items-center gap-2">
              <label class="text-sm font-medium text-gray-700">
                Contact Type:
              </label>
              <div class="flex gap-1 flex-wrap">
                <For each={contactTypes()}>
                  {(type) => (
                    <button
                      onClick={() => setSelectedContactType(type.value)}
                      class={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        selectedContactType() === type.value
                          ? "bg-blue-500 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {type.icon} {type.label}
                    </button>
                  )}
                </For>
              </div>
            </div>
          </div>
        </div>

        {/* Questions Table */}
        <div class="bg-white rounded-lg shadow overflow-hidden">
          <div class="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <h2 class="text-lg font-semibold text-gray-900">
              Template Questions
              <span class="ml-2 text-sm font-normal text-gray-500">
                ({questions().length} questions)
              </span>
            </h2>
            {/* Profile Badge */}
            <span
              class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white"
              style={{
                "background-color": currentProfile()?.accentColor || "#3B82F6",
              }}
            >
              {currentProfile()?.name}
            </span>
          </div>

          <Show when={loading()}>
            <div class="p-8 text-center">
              <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
              <p class="mt-2 text-gray-500">Loading questions...</p>
            </div>
          </Show>

          <Show when={error()}>
            <div class="p-4 bg-red-50 border-l-4 border-red-500">
              <p class="text-red-700">{error()}</p>
            </div>
          </Show>

          <Show when={!loading() && !error() && questions().length === 0}>
            <div class="p-8 text-center text-gray-500">
              No questions found for this profile and contact type.
            </div>
          </Show>

          <Show when={!loading() && !error() && questions().length > 0}>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      #
                    </th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                      Field Key
                    </th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Question
                    </th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Priority
                    </th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Importance
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <For each={questions()}>
                    {(q, index) => (
                      <tr class={index() % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <td class="px-4 py-3 text-sm text-gray-500">
                          {index() + 1}
                        </td>
                        <td class="px-4 py-3 text-sm font-mono text-gray-700">
                          {q.key}
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-900">
                          {q.question}
                        </td>
                        <td class="px-4 py-3">
                          <span
                            class={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getPriorityBadgeClass(
                              q.priority,
                            )}`}
                          >
                            {q.priority}
                          </span>
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-600">
                          {q.importance}
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>
        </div>

        {/* Footer Info */}
        <div class="mt-6 text-center text-sm text-gray-500">
          <p>
            Viewing template questions for{" "}
            <span class="font-medium">{currentProfile()?.name}</span> &gt;{" "}
            <span class="font-medium">
              {
                contactTypes().find((t) => t.value === selectedContactType())
                  ?.label
              }
            </span>
          </p>
          <p class="mt-1 text-xs">
            These questions are used to guide caseworkers during interviews.
          </p>
        </div>
      </main>
    </div>
  );
}
